/**
 * Internationalization (i18n) for checkcheck.com
 * Supports French (FR) and Dutch (NL)
 */

export const translations = {
  fr: {
    // Header & Navigation
    appName: "CheckCheck",
    tagline: "Vérifiez la santé financière",
    
    // Home page
    heroTitle: "Vérifiez une entreprise",
    heroSubtitle: "Score de fiabilité financière en quelques secondes",
    searchPlaceholder: "Nom de l'entreprise ou numéro de TVA",
    searchButton: "Vérifier",
    searchHint: "Ex: Colruyt, 0417497106",
    
    // Results page
    resultsTitle: "Résultats",
    resultsCount: "{count} entreprise(s) trouvée(s)",
    noResults: "Aucune entreprise trouvée",
    noResultsHelp: "Le nom commercial peut être différent du nom légal. Essayez avec le nom officiel ou le numéro de TVA indiqué sur le devis ou la facture.",
    noResultsTipTitle: "Astuce importante",
    noResultsTipIntro: "Le nom commercial peut être différent du nom légal de l'entreprise. Essayez avec :",
    noResultsTipOption1: "le nom officiel de l'entreprise ;",
    noResultsTipOption2: "ou le numéro de TVA indiqué sur le devis ou la facture.",
    tryAgain: "Essayez avec un autre terme de recherche",
    selectCompany: "Sélectionnez une entreprise",
    
    // Score page
    scoreTitle: "Analyse financière",
    score: "Score",
    rating: "Rating",
    outOf100: "/ 100",
    
    // Triple Check
    tripleCheck: "Triple Check",
    check1Title: "Structure financière",
    check2Title: "Stabilité",
    check3Title: "Risque global",
    
    // Status labels
    excellent: "Excellent",
    good: "Bon",
    average: "Moyen",
    fragile: "Fragile",
    critical: "Critique",
    
    // Confidence levels
    confidenceHigh: "Confiance élevée",
    confidenceMedium: "Confiance moyenne",
    confidenceLow: "Confiance faible",
    
    // Company info
    enterpriseNumber: "Numéro d'entreprise",
    legalForm: "Forme juridique",
    address: "Adresse",
    yearsAnalyzed: "Exercices analysés",
    
    // Messages
    recentCompany: "Entreprise récente",
    veryRecentCompany: "Entreprise très récente",
    noDataAvailable: "Aucune donnée disponible",
    
    // Actions
    newSearch: "Nouvelle recherche",
    back: "Retour",
    share: "Partager",
    
    // Footer
    dataSource: "Données basées sur les comptes annuels BNB et sur l'application d'une analyse approfondie des chiffres communiqués, ainsi que le KBO",
    disclaimer: "Score indicatif - ne constitue pas un conseil financier",
    
    // Loading states
    loading: "Chargement...",
    analyzing: "Analyse en cours...",
    
    // Errors
    errorGeneric: "Une erreur s'est produite",
    errorNoData: "Aucune donnée financière disponible",
    tryAgainLater: "Veuillez réessayer plus tard",
    
    // Language
    language: "Langue",
    french: "Français",
    dutch: "Nederlands"
  },
  
  nl: {
    // Header & Navigation
    appName: "CheckCheck",
    tagline: "Controleer de financiële gezondheid",
    
    // Home page
    heroTitle: "Controleer een bedrijf",
    heroSubtitle: "Financiële betrouwbaarheidsscore in enkele seconden",
    searchPlaceholder: "Naam van het bedrijf of btw-nummer",
    searchButton: "Controleren",
    searchHint: "Bijv: Colruyt, 0417497106",
    
    // Results page
    resultsTitle: "Resultaten",
    resultsCount: "{count} bedrijf(ven) gevonden",
    noResults: "Geen bedrijven gevonden",
    noResultsHelp: "De handelsnaam kan verschillen van de wettelijke naam. Probeer de officiële naam of het btw-nummer dat op de offerte of factuur staat.",
    noResultsTipTitle: "Belangrijke tip",
    noResultsTipIntro: "De handelsnaam kan verschillen van de wettelijke naam van het bedrijf. Probeer met:",
    noResultsTipOption1: "de officiële naam van het bedrijf;",
    noResultsTipOption2: "of het btw-nummer dat op de offerte of factuur staat.",
    tryAgain: "Probeer een andere zoekterm",
    selectCompany: "Selecteer een bedrijf",
    
    // Score page
    scoreTitle: "Financiële analyse",
    score: "Score",
    rating: "Rating",
    outOf100: "/ 100",
    
    // Triple Check
    tripleCheck: "Triple Check",
    check1Title: "Financiële structuur",
    check2Title: "Stabiliteit",
    check3Title: "Globaal risico",
    
    // Status labels
    excellent: "Uitstekend",
    good: "Goed",
    average: "Gemiddeld",
    fragile: "Kwetsbaar",
    critical: "Kritiek",
    
    // Confidence levels
    confidenceHigh: "Hoog vertrouwen",
    confidenceMedium: "Gemiddeld vertrouwen",
    confidenceLow: "Laag vertrouwen",
    
    // Company info
    enterpriseNumber: "Ondernemingsnummer",
    legalForm: "Rechtsvorm",
    address: "Adres",
    yearsAnalyzed: "Geanalyseerde boekjaren",
    
    // Messages
    recentCompany: "Recent bedrijf",
    veryRecentCompany: "Zeer recent bedrijf",
    noDataAvailable: "Geen gegevens beschikbaar",
    
    // Actions
    newSearch: "Nieuwe zoekopdracht",
    back: "Terug",
    share: "Delen",
    
    // Footer
    dataSource: "Gegevens gebaseerd op de NBB-jaarrekeningen en op een grondige analyse van de gepubliceerde cijfers, alsook de KBO",
    disclaimer: "Indicatieve score - geen financieel advies",
    
    // Loading states
    loading: "Laden...",
    analyzing: "Analyse bezig...",
    
    // Errors
    errorGeneric: "Er is een fout opgetreden",
    errorNoData: "Geen financiële gegevens beschikbaar",
    tryAgainLater: "Probeer het later opnieuw",
    
    // Language
    language: "Taal",
    french: "Français",
    dutch: "Nederlands"
  }
};

export const getStatusLabel = (status, lang) => {
  const labels = {
    excellent: translations[lang].excellent,
    good: translations[lang].good,
    average: translations[lang].average,
    fragile: translations[lang].fragile,
    critical: translations[lang].critical
  };
  return labels[status] || status;
};

export const getConfidenceLabel = (level, lang) => {
  const labels = {
    high: translations[lang].confidenceHigh,
    medium: translations[lang].confidenceMedium,
    low: translations[lang].confidenceLow
  };
  return labels[level] || level;
};
